import type {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  NextPage,
} from "next";
import { MainLayout } from "src/presentation/layouts";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { settings } from "@config/settings";
import { localesParserDictionary } from "@utils/locales-parser";
import { EntitiesFetcherService } from "@services/entities-fetcher";
import { useFamilias } from "src/context/useFamilias";
import styles from "./productos-page.module.scss";
import { Key, useEffect, useRef, useState } from "react";
import SearchIcon from "@assets/icons/search.svg";
import FilterLogo from "@assets/icons/filter.svg";
import classNames from "classnames";
import {
  FamiliaBanner,
  FamiliasDropdown,
  ProductCard,
} from "src/presentation/components/organisms";
import { useProductos } from "src/context/useProductos";
import { Familia } from "@models/familias";
import { useTranslation } from "next-i18next";
import { EcoSwitch } from "src/presentation/components/organisms/eco-switch";
import { NextSeo } from "next-seo";
import { MainRouter } from "@config/urls";
import { useRouter } from "next/router";
import { cleanUrl } from "@utils/url-cleaner";
import { AnalitycsService } from "@services/analytics-service";
import { useProductosGenericos } from "src/context/useProductosGenericos";
import useFilteredProducts from "src/context/useFilteredProducts";
import { filterAndSortProducts } from "@utils/filterAndSortProducts";

const Home: NextPage = (props: any) => {
  const { country, filteredAndSortedProducts } = props;
  const router = useRouter();
  const { familia } = router.query;
  const families = useFamilias();

  const productosFinal = filteredAndSortedProducts;
  const { t } = useTranslation();
  const [inputFilter, setInputFilter] = useState("");
  const [familyFilter, setFamilyFilter] = useState<Familia | null>(
    familia
      ? families.find((x) => `${cleanUrl(x.nombre)}` === familia) || null
      : null
  );

  const [filtersHidden, setFiltersHidden] = useState(true);
  const [ecoFilter, setEcoFilter] = useState(false);
  const searchRef = useRef(null);
  useEffect(() => {
    if (familia) {
      setFamilyFilter(
        families.find((x) => `${cleanUrl(x.nombre)}` === familia) || null
      );
    }
  }, [familia, families]);
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (inputFilter.length > 0) {
        new AnalitycsService().trackEvent({
          category: "Productos",
          action: "BusquedaRealizada",
          label: inputFilter,
        });
      }
    }, 2500);
    return () => clearTimeout(delayDebounceFn);
  }, [inputFilter]);

  return (
    <>
      <NextSeo
        title={`${
          familyFilter
            ? "manvert | " + familyFilter.nombre
            : t("SEOMeta.ProductosView.title")
        }`}
        description={`${
          familyFilter
            ? familyFilter.descripcion
            : t("SEOMeta.ProductosView.description")
        }`}
      />
      <MainLayout
        breadcrumb={[
          {
            label: t("urls.home"),
            url: MainRouter.home.generatePath(),
          },
          {
            label: t("urls.productos"),
            url: `/${country}/${MainRouter.productos.generatePath()}`,
          },
        ]}
      >
        <div className={styles.root}>
          <div className={styles.headerbar}>
            <div className={styles.searchContainer}>
              <div className={styles.searchinput}>
                <SearchIcon height={25} />
                <form>
                  <input
                    ref={searchRef}
                    value={inputFilter}
                    onChange={(evt) => {
                      setInputFilter(evt.target.value);
                    }}
                    placeholder={t("ProductosView.searchPlaceholder") || ""}
                  />
                </form>
              </div>
              <button
                onClick={() => {
                  setFiltersHidden(!filtersHidden);
                }}
                className={classNames(styles.filterButton)}
              >
                <FilterLogo />
                <p>{t("ProductosView.filters")}</p>
              </button>
            </div>
            <div
              className={classNames(styles.filtersContainer, {
                [styles.mobileHidden]: filtersHidden,
              })}
            >
              <div className={styles.family}>
                <FamiliasDropdown
                  familia={familyFilter}
                  onChange={(newValue: any) => {
                    const fam = families.find((x) => x.nombre === newValue);
                    setFamilyFilter(fam ? fam : null);
                  }}
                  card
                />
              </div>
              <div className={styles.eco}>
                <EcoSwitch
                  status={ecoFilter}
                  onChange={() => {
                    setEcoFilter(!ecoFilter);
                  }}
                />
              </div>
            </div>
          </div>
          <div className={styles.productsgrid}>
            {families && familyFilter && (
              <div className={styles.familywrapper}>
                <FamiliaBanner
                  title={familyFilter.nombre}
                  paragraphs={[familyFilter.descripcion]}
                  image={familyFilter.imagen}
                />
              </div>
            )}
            {productosFinal &&
              productosFinal
                .filter((item: { nombre: string; descripcion2: string }) => {
                  return (
                    item.nombre
                      .toLowerCase()
                      .indexOf(inputFilter.toLowerCase()) !== -1 ||
                    item.descripcion2
                      .toLowerCase()
                      .indexOf(inputFilter.toLowerCase()) !== -1
                  );
                })
                .filter((item: { familia: number }) => {
                  if (!familyFilter) return true;
                  return item.familia === familyFilter.id;
                })
                .filter((item: { ecologico: any }) => {
                  if (ecoFilter) {
                    return item.ecologico;
                  }
                  return true;
                })

                .map(
                  (item: {
                    id: number;
                    slug: string | undefined;
                    nombre: string | undefined;
                    ecologico: boolean | undefined;
                    internacional: boolean | undefined;
                    familia: number;
                    imagen_catalogo: string | undefined;
                    descripcion2: string;
                  }) => {
                    return (
                      <div key={item.id} className={styles.productWrapper}>
                        <ProductCard
                          id={item.id}
                          slug={item.slug}
                          name={item.nombre}
                          eco={item.ecologico}
                          internacional={item.internacional}
                          category={
                            families.find((x) => x.id === item.familia)?.nombre
                          }
                          categoryColor={
                            families.find((x) => x.id === item.familia)?.color
                          }
                          country={country}
                          image={item.imagen_catalogo}
                          descripcion={[item.descripcion2]}
                          animated
                          card
                        />
                      </div>
                    );
                  }
                )}
          </div>
        </div>
      </MainLayout>
    </>
  );
};

export async function getServerSideProps(
  Props: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<any>> {
  const { locale, req, query } = Props;
  const host = `https://${req.headers.host}`;
  const { country } = query;

  const countryCode = country?.toString() || settings.DEFAULT_COUNTRY;

  const opts = {
    locale: localesParserDictionary[locale || settings.DEFAULT_LANG],
    country: countryCode,
  };

  const entities = await new EntitiesFetcherService().getEntities({
    ...opts,
    productos: true,
    productosDestacados: true,
    productosGenericos: true,
  });

  // Obtener productos y productos genéricos
  const productosData = entities.productosData || [];
  const productosGenericosData = entities.productosGenericosData || [];
  const filteredAndSortedProducts = filterAndSortProducts(
    productosData,
    productosGenericosData
  );

  return {
    props: {
      host: host,
      country: countryCode,
      productosData: JSON.stringify(entities.productosData),
      productosDestacadosData: JSON.stringify(entities.productosDestacadosData),
      productosGenericosData: JSON.stringify(entities.productosGenericosData),
      filteredAndSortedProducts: JSON.parse(
        JSON.stringify(filteredAndSortedProducts)
      ),
      ...(await serverSideTranslations(locale || settings.I18N_FALLBACK, [
        "common",
      ])),
    },
  };
}

export default Home;
